import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
} from "@mui/material";
import axios from "axios";
import { Helmet } from "react-helmet";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import PlayListExportContent from "../Contents/PlayListExportContent.js";
import { message } from "../Constants/message.js";
import UpgradeModal from "../Components/UpgradeModal";
import { useNavigate } from "react-router-dom";
import CommentSection from "../Components/CommentSection.js";

function PlayListExport({ user, login, isLoggedIn }) {
   const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setPlaylistUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setPlaylistUrl(newUrl);
  };

  const validateUrl = (url) => {
    const isValid = url.includes("youtube.com") && url.includes("list=");
    if (!isValid) setError("Please enter a valid YouTube playlist URL. It should be like https://www.youtube.com/watch?v=VIDEO_ID&list=PLAYLIST_ID");
    setIsValidUrl(isValid);
    return isValid;
  };

  const handleExport = async () => {
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }

    setSuccess("");
    setError("");

    if (!validateUrl(playlistUrl)) return;

    if (credits.remaining <= 0) {
      setError("You don’t have enough credits to export a playlist.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/playlistExport`,
        { playlistInput: playlistUrl },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      setLoading(false);
      setTimeout(() => setSuccess(response.data.successMessage), 200);

      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });

      const fileName = response.data.fileName;
      if (!fileName && response.data.email_send === true) {
        return setSuccess(response.data.successMessage);
      } else if (!fileName) {
        return setError("No file available for download.");
      }

      const downloadResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/playlistExport/download`,
        { fileName },
        {
          withCredentials: true,
          responseType: "blob",
        }
      );
      const blob = new Blob([downloadResponse.data], {
        type: "application/octet-stream",
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadUrl(downloadUrl);
      if ([0, 2, 4, 6].includes(credits.availed) && user.account_type.toLowerCase() === "free") {
        setTimeout(() => {
          setShowUpgradeModal(true);
        }, 2300); 
      }
     
    } catch (err) {
      setError(
        err.response?.data?.errorMessage ||
          "Failed to export playlist. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Export YouTube Playlist to CSV | Free Playlist Exporter</title>
        <meta
          name="description"
          content="Convert your YouTube playlist to CSV or Excel in one click. Export video titles, links, and more – free and fast. No software needed."
        />
        <meta
          name="keywords"
          content="YouTube playlist to CSV, export YouTube playlist, download YouTube playlist data, YouTube to Excel"
        />
        <link
          rel="canonical"
          href="https://yourdomain.com/youtube-playlist-export"
        />
      </Helmet>

      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h1" align="center" sx={{ fontSize: "3rem" }}>
            Export YouTube Playlist to CSV
          </TitleText>

          <SubtitleText variant="h2" align="center" sx={{ fontSize: "1.2rem" }}>
            Convert Your Playlist to Excel – Fast, Secure & Easy
          </SubtitleText>

          <Box mt={2} mb={3}>
            <TextField
              fullWidth
              label="Paste Your YouTube Playlist URL"
              variant="outlined"
              value={playlistUrl}
              onChange={handleUrlChange}
              placeholder="https://www.youtube.com/watch?v=VIDEO_ID&list=PLAYLIST_ID"
              size="medium"
              id="youtube-playlist-url"
              name="primary-textbox"
            />
          </Box>

          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            <Typography variant="body2" color="text.secondary">
              ✅ <b>One-click Export = One Credit</b>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ⏱️ Large playlists? We’ll email you the file automatically.
            </Typography>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}{" "}
              {downloadUrl && (
                <>
                  <br />
                  If your download hasn’t started,{" "}
                  <a href={downloadUrl} download="playlist.csv">
                    click here to download manually
                  </a>
                  .
                </>
              )}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2} mt={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleExport}
            >
              Export Playlist Now
            </StyledButton>
          </Box>

          <Box mt={4}>
            <Typography variant="body2" color="text.secondary" align="center">
              Ensure your playlist is public or unlisted. If its private, make it public and try again. Need help?{" "}
              <Link to="/contact" color="primary">
                Contact Support
              </Link>
            </Typography>
          </Box>
        </ContentBox>
          <CommentSection
                            toolId="youtube/playlistExport"
                            user={user}
                            login={login}
                            isLoggedIn={isLoggedIn}
                          />
        <PlayListExportContent />
        <UpgradeModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={() => {
            navigate("/Pricing");
            setShowUpgradeModal(false);
          }}
          title="🎉 Hurry! 50% OFF on Annual Plans!"
          subTitle="Upgrade now and export complete Playlist to CSV."
        />
      </PageContainer>
    </>
  );
}

export default PlayListExport;
